import React, { createContext } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import useSessionStorage from 'utils/useSessionStorage'

const VendorWizardContext = createContext<VendorWizardResponse>({} as VendorWizardResponse)

type VendorWizardResponse = {
  vendorUser: VendorWizardUser
  setVendorUser: (value: VendorWizardUser) => void
  company: VendorWizardCompany | null
  setCompany: (value: VendorWizardCompany) => void
  vendorInfo: VendorWizardCompanyInfo
  setVendorInfo: (value: VendorWizardCompanyInfo) => void
  approvedRepresentationRightBusinessId: string | null
  setApprovedRepresentationRightBusinessId: (value: string | null) => void
  initVendorInfo: (company: VendorResponseType) => void
  initVendorUser: (user: UserResponseType) => void
  handleCompanyChange: (value: SearchCompaniesResponseType | null) => void
}

type VendorWizardUser = {
  firstname: string
  lastname: string
  phone: string
  email: string
}

type VendorWizardCompany = {
  name: string
  businessId: string
}

type VendorWizardCompanyInfo = {
  displayName: string
  websiteUrl: string
  description: string
  ccEmail: string
  contactResponsibilityDescription: string
  contactIsDecisionMaker: boolean
  contactReceivesRequestForProposals: boolean
}

const initialVendorInfo: VendorWizardCompanyInfo = {
  displayName: '',
  websiteUrl: '',
  description: '',
  ccEmail: '',
  contactResponsibilityDescription: '',
  contactIsDecisionMaker: true,
  contactReceivesRequestForProposals: true
}

export const VendorWizardProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { user: auth0User } = useAuth0()

  const [vendorUser, setVendorUser] = useSessionStorage<VendorWizardUser>('proc-user', {
    firstname: '',
    lastname: '',
    phone: '',
    email: auth0User?.email ?? ''
  })

  const [company, setCompany] = useSessionStorage<VendorWizardCompany | null>('proc-company', null)

  const [approvedRepresentationRightBusinessId, setApprovedRepresentationRightBusinessId] = useSessionStorage<
    string | null
  >('proc-approved-representation-right-business-id', null)

  const [vendorInfo, setVendorInfo] = useSessionStorage<VendorWizardCompanyInfo>('proc-company-info', initialVendorInfo)

  const initVendorUser = React.useCallback(
    (user: UserResponseType) => {
      const { firstname, lastname, phone } = vendorUser

      // Make sure you don't override sessionstorage data
      setVendorUser({
        ...vendorUser,
        firstname: firstname ? firstname : user.firstName,
        lastname: lastname ? lastname : user.lastName,
        phone: phone ? phone : user.phone
      })
    },
    [setVendorUser, vendorUser]
  )

  const initVendorInfo = React.useCallback(
    (vendorCompany: VendorResponseType) => {
      const { displayName, websiteUrl, description, ccEmail } = vendorInfo

      // Make sure you don't override sessionstorage data
      setVendorInfo({
        ...vendorInfo,
        displayName: displayName || vendorCompany.displayName || company?.name || '',
        websiteUrl: websiteUrl || vendorCompany.websiteUrl || '',
        description: description || vendorCompany.description || '',
        ccEmail: ccEmail || vendorCompany.ccEmail || ''
      })
    },
    [company?.name, setVendorInfo, vendorInfo]
  )

  const handleCompanyChange = React.useCallback(
    (value: SearchCompaniesResponseType | null): void => {
      sessionStorage.clear()
      const result = value
      setApprovedRepresentationRightBusinessId(null)
      setVendorInfo(initialVendorInfo)
      setCompany(result)
    },
    [setApprovedRepresentationRightBusinessId, setCompany, setVendorInfo]
  )

  const value = React.useMemo(
    () => ({
      vendorUser,
      setVendorUser,
      company,
      setCompany,
      approvedRepresentationRightBusinessId,
      setApprovedRepresentationRightBusinessId,
      vendorInfo,
      setVendorInfo,
      initVendorInfo,
      initVendorUser,
      handleCompanyChange
    }),
    [
      vendorUser,
      setVendorUser,
      company,
      setCompany,
      approvedRepresentationRightBusinessId,
      setApprovedRepresentationRightBusinessId,
      vendorInfo,
      setVendorInfo,
      initVendorInfo,
      initVendorUser,
      handleCompanyChange
    ]
  )

  return <VendorWizardContext.Provider value={value}>{children}</VendorWizardContext.Provider>
}

export const useVendorWizard = (): VendorWizardResponse => {
  const context = React.useContext(VendorWizardContext)
  if (context === undefined) {
    throw new Error('useVendorWizard must be used within a VendorWizardProvider')
  }

  return context
}
